import { Participant } from "./participant";
import { SessionTypes, ParticipantTypes } from "./types";
import { getLogger } from "@/lib/logger";

const logger = getLogger("webrtc:participant");

const VIDEO_FREEZE_THRESHOLD_MS = 20000;  // if video is frozen for this long, disconnect

export class VideoDeviceParticipant extends Participant {
  /**
   * 
   * @param {string} peerId - the id of the peer
   * @param {RTCPeerConnection} rtcPeerConnection - the RTCPeerConnection object 
   * @param {ConnectionContext} context - the connection context
   * @param {int} videoDeviceId - the id of the video device
   * @param {function} dataChannelMessageListener - listener for data channel messages
   * @param {boolean} sendVideo - whether to send video (for devices that support it)
   */
  constructor({ peerId, rtcPeerConnection, context, videoDeviceId, dataChannelMessageListener, sendVideo }) {
    super({ peerId, rtcPeerConnection, context, participantType: ParticipantTypes.device, dataChannelMessageListener });
    this.videoDeviceId = videoDeviceId;  // numeric API id of the video device
    this.videoFrozenAt = null;  // timestamp of when the video was frozen
    this.sendVideo = sendVideo;  // whether to send video (for devices that support it)
  }

  async connectLocalMedia() {
    const localMediaStream = this.context.localMediaStream;
    if (localMediaStream) {
      logger.debug('local media stream available', { device_id: this.videoDeviceId });
      // the stream may have video but that may be separately sent to a kiosk connection
      localMediaStream.getAudioTracks().forEach((track) => {
        if (this.context.sessionType === SessionTypes.monitoring) {
          track.enabled = false;  // disable audio for monitoring
          logger.debug('audio track disabled for monitoring', { device_id: this.videoDeviceId });
        }
        super.addLocalMediaTrack(track, localMediaStream);
      });
      if (this.sendVideo) {
        logger.debug('connecting video tracks', { device_id: this.videoDeviceId });
        localMediaStream.getVideoTracks().forEach((track) => {
          super.addLocalMediaTrack(track, localMediaStream);
        });
      }
    } else if (this.context.sessionType !== SessionTypes.test) {
      logger.error('no local media stream available', { device_id: this.videoDeviceId });
      this.emit("error", "no-local-media-stream");
    }
  }

  configurePeerConnectionListeners() {
    super.configurePeerConnectionListeners();
    const peerConn = this.rtcPeerConnection;

    // special handling for video device (patient) connections
    peerConn.addEventListener("track", (event) => {
      event.track.onmute = () => {
        logger.warn('remote track muted, possible freeze', { device_id: this.videoDeviceId, peer_id: this.peerId, kind: event.track.kind });
        this.videoFrozenAt = Date.now();
        setTimeout(() => {
          logger.debug('checking video freeze state', { device_id: this.videoDeviceId, frozen_at: this.videoFrozenAt });
          if (this.videoFrozenAt && Date.now() - this.videoFrozenAt >= (VIDEO_FREEZE_THRESHOLD_MS - 250)) {
            logger.error('video still frozen, disconnecting', { device_id: this.videoDeviceId, peer_id: this.peerId });
            this.emit("error", "patient-video-frozen");
          } else if (this.videoFrozenAt) {
            logger.debug('video still frozen, newer check pending', { device_id: this.videoDeviceId });
          } else {
            logger.info('video track recovered', { device_id: this.videoDeviceId, peer_id: this.peerId });
          }
        }, VIDEO_FREEZE_THRESHOLD_MS);
      }
      event.track.onunmute = () => {
        logger.info('remote track unmuted', { device_id: this.videoDeviceId, peer_id: this.peerId, kind: event.track.kind });
        this.videoFrozenAt = null;
      }
      // a remote track ending or leaving the stream is otherwise completely silent: no mute,
      // no connection state change. It does NOT fire on our own pc.close()/track.stop()
      // (the spec ends tracks silently there), so this only logs sender-side track death.
      event.track.onended = () => {
        logger.error('remote track ended', { device_id: this.videoDeviceId, peer_id: this.peerId, kind: event.track.kind, track_id: event.track.id });
      }
      // property assignment (not addEventListener): this track handler runs once per track
      // (audio + video) with the same streams[0], so a listener would attach twice
      event.streams[0].onremovetrack = (removeEvent) => {
        logger.error('remote track removed from stream', { device_id: this.videoDeviceId, peer_id: this.peerId, kind: removeEvent.track.kind, track_id: removeEvent.track.id });
      }
    })

    logger.debug('device peer connection listeners configured', { device_id: this.videoDeviceId });
  }
}